import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { colors as themeColors } from 'src/theme';
import { InputLabel, CheckIcon } from 'src/components';
import { ValidationErrorStyle } from 'src/common';

const Container = styled.div<{ left?: boolean, }>(({ left }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
  flexDirection: left ? 'row-reverse' : 'row',
}));

const TextContainer = styled.div<{ disabled: boolean }>(({ disabled }) => ({
  flex: 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
}));

const CheckboxContainer = styled.div({
  position: 'relative',
  width: '24px',
  height: '24px',
  flexShrink: 0,
});

const CheckboxBox = styled.div<{ isSelected: boolean; disabled: boolean }>(({ isSelected, disabled }) => ({
  width: '24px',
  height: '24px',
  border: `2px solid ${isSelected ? themeColors.primary1 : themeColors.border3}`,
  backgroundColor: isSelected ? themeColors.primary1 : themeColors.white,
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: `${disabled ? 0.5 : 1} !important`,
  transition: 'all 0.2s ease-in-out',
  cursor: disabled ? 'not-allowed' : 'pointer',
}));

const InputLabelStyle = styled(InputLabel)({
  marginBottom: '0px',
  lineHeight: '22px',
});

const DescriptionText = styled.div({
  fontSize: '12px',
  color: themeColors.text4,
  lineHeight: '16px',
});

export type CheckboxInputProps = {
  label?: string;
  description?: string;
  value?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  error?: string;
  className?: string;
  left?: boolean;
  noText?: boolean;
  labelComponent?: React.ReactNode;
};

export const CheckboxInput: React.FC<CheckboxInputProps> = ({
  label,
  description,
  value = false,
  onChange,
  disabled = false,
  error = '',
  className,
  left = false,
  noText = false,
  labelComponent,
}) => {
  const [isSelected, setIsSelected] = useState<boolean>(value);

  useEffect(() => {
    setIsSelected(value);
  }, [value]);

  const handleToggle = () => {
    if (disabled) return;
    const newValue = !isSelected;
    setIsSelected(newValue);
    if (onChange) onChange(newValue);
  };

  return (
    <Container className={className} left={left} onClick={handleToggle}>
      { !noText &&
        <TextContainer disabled={disabled}>
          {label && !labelComponent && <InputLabelStyle disabled={disabled}>{label}</InputLabelStyle>}
          {labelComponent && labelComponent}
          {description && <DescriptionText>{description}</DescriptionText>}
          {error && <ValidationErrorStyle>{error}</ValidationErrorStyle>}
        </TextContainer>
      }
      
      <CheckboxContainer>
        <CheckboxBox isSelected={isSelected} disabled={disabled}>
          {isSelected && <CheckIcon color={themeColors.white} width={18} height={18} />}
        </CheckboxBox>
      </CheckboxContainer>
      
    </Container>
  );
};
